import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, ASSETS } from "@/lib/api";

// Fallback ONLY. Live chips come from `/api/site/signature-genres` (authoritative Genre Pages).
const SIGNATURE_FALLBACK = [
  "EDM (Festival / Club)", "Afro House", "Afro Tech", "Reggaeton",
  "Trap", "Techno", "Moombahton", "Big Room EDM", "Future Rave",
];

function useSignatureGenres() {
  const [sigGenres, setSigGenres] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/site/signature-genres")
      .then(({ data }) => !cancelled && setSigGenres((data?.items || []).map((g) => g.display_name).filter(Boolean)))
      .catch(() => !cancelled && setSigGenres([]));
    return () => { cancelled = true; };
  }, []);
  return sigGenres && sigGenres.length ? sigGenres : SIGNATURE_FALLBACK;
}

// Balanced two-column layout: dominant original portrait + one text column (heading, bio, genres).
export default function About() {
  const { t } = useTranslation();
  const chips = useSignatureGenres();
  return (
    <section id="about" data-testid="section-about" className="relative py-24 md:py-32 px-6 md:px-10 bg-black">
      <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
        <figure className="relative overflow-hidden rounded-2xl hairline aspect-[4/5] w-full max-w-[520px] mx-auto md:mx-0" data-testid="about-portrait">
          <img src={ASSETS.portrait1} alt="DJ SPY portrait — Dubai DJ and music producer" loading="lazy" decoding="async"
            className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
          <figcaption className="absolute bottom-6 inset-x-6">
            <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand">{t("about.signatureBadge")}</div>
            <div className="font-anton text-3xl md:text-4xl text-white mt-2 leading-none" data-testid="about-quote">{t("about.signatureQuote")}</div>
          </figcaption>
        </figure>

        <div className="text-start" data-testid="about-text">
          <p className="font-mono text-xs text-red-brand tracking-[0.3em] uppercase">{t("about.kicker")}</p>
          <h2 className="font-anton mt-4 text-5xl md:text-6xl leading-none text-white">{t("about.title")}</h2>
          <p className="mt-8 text-white/80 leading-relaxed text-base md:text-lg">{t("about.body1")}</p>
          <p className="mt-4 text-white/55 leading-relaxed">{t("about.body2")}</p>
          <div className="mt-8 pt-6 border-t border-white/10">
            <div className="font-mono text-[10px] tracking-[0.3em] text-white/45 mb-3">{t("about.signatureGenres")}</div>
            <div className="flex flex-wrap gap-1.5" data-testid="about-signature-genres">
              {chips.map((g) => (
                <span key={g} className="px-2.5 py-1 rounded-full border border-white/15 text-white/80 text-[11px] font-mono">{g}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
